/**
 * Users endpoints — port of v1 ``ando-frontend/app/api/users.ts``.
 *
 * BE source of truth: ``ando-agent/src/api/v1/endpoints/users.py``.
 *
 *   GET   /users/{user_id}              → profile + preferences
 *   PATCH /users/{user_id}/preferences  → sparse merge of preferences
 *   GET   /users/{user_id}/limits       → plan limits + usage
 *
 * The BE does NOT yet ship endpoints for password change or MFA enroll/verify
 * (the only auth-side endpoints are ``/auth/refresh``, ``/auth/logout``).
 * Security flows live in Zitadel — the Settings → Security tab links out to
 * the Zitadel hosted UI instead of duplicating the form here.
 */

import { apiClient } from "../ApiClient.js";

// ── Wave 3 — Proactive Layer nudge settings ────────────────────────────

export type NudgeCategoryKey =
  | "deadline_reminder"
  | "forgotten_task"
  | "pattern_break"
  | "insight"
  | "encouragement"
  | "support";

export type NudgeCategoryFlags = Record<NudgeCategoryKey, boolean>;

export interface NudgeCaps {
  in_app_soft?: number;
  in_app_hard?: number;
  email_soft?: number;
  email_hard?: number;
}

/** Opt-OUT model (Q5=1 — all categories ON by default). */
export const DEFAULT_NUDGE_CATEGORIES: NudgeCategoryFlags = {
  deadline_reminder: true,
  forgotten_task: true,
  pattern_break: true,
  insight: true,
  encouragement: true,
  support: true,
};

/** Defaults must mirror the BE ``TrustBudget`` constants (3/5 in-app, 1/2 email). */
export const DEFAULT_NUDGE_CAPS: Required<NudgeCaps> = {
  in_app_soft: 3,
  in_app_hard: 5,
  email_soft: 1,
  email_hard: 2,
};

// ── User profile ────────────────────────────────────────────────────────

export interface UserPreferences {
  language?: string;
  theme?: string;
  email_notifications?: boolean;
  selected_folder_ids?: string[];
  default_conversation_folders?: string[];
  nudge_categories?: Partial<NudgeCategoryFlags>;
  nudge_email_opt_in?: boolean;
  nudge_caps?: NudgeCaps;
  /** Write-side: setting this (via PATCH) routes to ``metadata.onboarding_seen``
   *  on the BE. Read it back from ``metadata.onboarding_seen`` below. */
  onboarding_seen?: boolean;
  /** JSONB blob surfaced by the BE — holds nudge settings + ``onboarding_seen``. */
  metadata?: { onboarding_seen?: boolean } & Record<string, unknown>;
  [key: string]: unknown;
}

export type UserPreferencesUpdate = Partial<UserPreferences>;

export interface UserProfileResponse {
  user_id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  preferences: UserPreferences;
  created_at: string;
  /**
   * Wave 3 (Jul 2026) memory consent surface — populated by
   * ``ando-agent/src/api/v1/endpoints/users.py::get_user`` (both nullable):
   *  - ``memory_consent_at``: ISO timestamp when the user explicitly
   *    opted-in. ``null`` means Tier 1 self-facts extraction is gated OFF.
   *  - ``memory_consent_banner_shown_at``: ISO timestamp of the FIRST
   *    banner acknowledgement (accept OR "not now"). ``null`` means the
   *    user has NEVER seen the banner → ``MemoryConsentBanner`` renders.
   */
  memory_consent_at?: string | null;
  memory_consent_banner_shown_at?: string | null;
}

export interface UpdatePreferencesResponse {
  success: boolean;
  preferences: UserPreferences;
}

export interface UserLimitsResponse {
  tier_name: string;
  documents: Record<string, unknown>;
  queries: Record<string, unknown>;
  storage: Record<string, unknown>;
}

// ── Endpoint helpers ───────────────────────────────────────────────────

export async function getUserProfile(userId: string): Promise<UserProfileResponse> {
  return apiClient.get<UserProfileResponse>(
    `/users/${encodeURIComponent(userId)}`,
  );
}

export async function updateUserPreferences(
  userId: string,
  update: UserPreferencesUpdate,
): Promise<UpdatePreferencesResponse> {
  return apiClient.patch<UpdatePreferencesResponse>(
    `/users/${encodeURIComponent(userId)}/preferences`,
    update,
  );
}

export async function getUserLimits(userId: string): Promise<UserLimitsResponse> {
  return apiClient.get<UserLimitsResponse>(
    `/users/${encodeURIComponent(userId)}/limits`,
  );
}

// ── GDPR Articles 15 + 17 — Right of Access + Right to Erasure ─────────
//
// BE source of truth: ``ando-agent/src/api/v1/endpoints/gdpr.py``.
//
//   POST   /gdpr/export              → kick off data-export background job
//   GET    /gdpr/export/{export_id}  → poll job status / fetch download URL
//   DELETE /gdpr/account             → schedule full account deletion
//
// Spec note (May 2026): the v1 components called ``/api/v1/users/me/export``
// and ``DELETE /users/me`` — those routes never shipped on the BE.
// The canonical routes are under ``/gdpr/*`` and we mirror them verbatim
// so failure modes map 1:1 to backend errors.

/** Possible states of a GDPR export job. BE returns ``processing | completed | failed``. */
export type DataExportStatus = "processing" | "completed" | "failed";

export interface DataExportResponse {
  readonly export_id: string;
  readonly status: DataExportStatus;
  readonly requested_at: string;
  readonly download_url?: string | null;
  readonly expires_at?: string | null;
}

export interface DataExportRequestBody {
  /** ``"json"`` (default) or ``"csv"``. */
  readonly format?: "json" | "csv";
  /** If set, the BE also emails the export link here when ``ready``. */
  readonly email_to?: string;
}

/** ``POST /gdpr/export`` — schedule the export job (Article 15). */
export async function requestDataExport(
  body: DataExportRequestBody = {},
): Promise<DataExportResponse> {
  return apiClient.post<DataExportResponse>("/gdpr/export", {
    format: body.format ?? "json",
    ...(body.email_to !== undefined ? { email_to: body.email_to } : {}),
  });
}

/** ``GET /gdpr/export/{export_id}`` — poll until ``status !== "processing"``. */
export async function getDataExportStatus(
  exportId: string,
): Promise<DataExportResponse> {
  return apiClient.get<DataExportResponse>(
    `/gdpr/export/${encodeURIComponent(exportId)}`,
  );
}

export interface DeleteAccountBody {
  /** Must equal the user's account email — BE enforces this. */
  readonly confirm_email: string;
  /** Optional free-text reason; surfaced in the audit log. */
  readonly reason?: string;
}

export interface DeleteAccountResponse {
  readonly deletion_id: string;
  readonly status: "scheduled" | "processing" | "completed";
  readonly scheduled_for: string;
  readonly data_retained_until: string;
}

/** ``DELETE /gdpr/account`` — Article 17 right to erasure. */
export async function deleteAccount(
  body: DeleteAccountBody,
): Promise<DeleteAccountResponse> {
  return apiClient.request<DeleteAccountResponse>("/gdpr/account", {
    method: "DELETE",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json" },
  });
}

// ── Wave 2 — Consent + memory (facts + right-to-be-forgotten) ──────────
//
// BE source of truth: ``ando-agent/src/api/v1/endpoints/gdpr.py``.
//
//   GET  /gdpr/memory-facts    → per-category memory items surfaced to the user
//   POST /gdpr/memory-erasure  → schedule a memory-only erasure (audit-logged)
//
// ``ConsentPreferences`` mirrors the BE consent JSON. ``memory_enabled`` is
// the Wave 2 opt-in that gates the memory-facts extraction pipeline; when
// ``false`` the ``GET /gdpr/memory-facts`` endpoint returns
// ``status: "consent_required"`` and an empty ``facts_by_category``.

export interface ConsentPreferences {
  analytics?: boolean;
  marketing_emails?: boolean;
  product_updates?: boolean;
  ai_training?: boolean;
  /** Wave 2 — gates the memory-facts pipeline (GET /gdpr/memory-facts). */
  memory_enabled?: boolean;
}

export interface MemoryFactItem {
  readonly predicate: string;
  readonly object: string;
  readonly confidence: number | null;
  readonly salience: number | null;
  readonly valid_at: string | null;
}

export interface MemoryFactsResponse {
  readonly status: "ok" | "consent_required" | "error";
  readonly facts_by_category: Record<string, MemoryFactItem[]>;
  readonly total_facts: number;
  readonly last_extracted_at: string | null;
  readonly memory_consent_at: string | null;
}

/** ``GET /gdpr/memory-facts`` — surfaces extracted facts grouped by category. */
export async function getMemoryFacts(): Promise<MemoryFactsResponse> {
  return apiClient.get<MemoryFactsResponse>("/gdpr/memory-facts");
}

export interface MemoryErasureRequest {
  /** Free-text reason, persisted in the audit trail. */
  readonly reason: string;
  /** BE-enforced confirmation phrase (see gdpr.py for the exact literal). */
  readonly confirm_phrase: string;
}

export interface MemoryErasureResponse {
  readonly status: "ok" | "error";
  readonly pending_facts: number;
  readonly audit_id: string;
  readonly requested_at: string;
}

/** ``POST /gdpr/memory-erasure`` — memory-only right-to-be-forgotten. */
export async function requestMemoryErasure(
  body: MemoryErasureRequest,
): Promise<MemoryErasureResponse> {
  return apiClient.post<MemoryErasureResponse>("/gdpr/memory-erasure", body);
}

// ── PATCH /gdpr/consent ────────────────────────────────────────────────
//
// BE contract: ``ando-agent/src/api/v1/endpoints/gdpr.py::update_consent_preferences``.
// Sparse PATCH — omitted fields are ignored. Passing ``memory_enabled: false``
// simultaneously clears ``users.memory_consent_at`` and fires the erasure
// pipeline (reason=``consent_revoked``); passing ``true`` stamps the consent
// timestamp. The BE returns the updated consent JSON + ``memory_consent_at``
// so the FE can render "attivo dal <date>" without a refetch.

export interface UpdateConsentResponse {
  readonly success: boolean;
  readonly consent: {
    readonly analytics?: boolean | null;
    readonly marketing_emails?: boolean | null;
    readonly product_updates?: boolean | null;
    readonly ai_training?: boolean | null;
    readonly memory_enabled?: boolean | null;
  };
  readonly memory_consent_at: string | null;
  readonly updated_at: string;
}

/** ``PATCH /gdpr/consent`` — sparse-update consent preferences. */
export async function updateConsent(
  body: ConsentPreferences,
): Promise<UpdateConsentResponse> {
  return apiClient.patch<UpdateConsentResponse>("/gdpr/consent", body);
}

// ── Wave 3 — Memory consent banner acknowledge ──────────────────────────
//
// BE: ``ando-agent/src/api/v1/endpoints/gdpr.py::acknowledge_memory_consent``
//
// UX contract:
// - ``accepted=true``  → BE writes ``memory_consent_at=NOW()`` +
//   ``memory_consent_banner_shown_at=NOW()``. Tier 1 self-facts extraction
//   starts on the next chat turn.
// - ``accepted=false`` → BE writes ``memory_consent_banner_shown_at=NOW()``
//   only (leaves ``memory_consent_at`` unchanged / NULL). The user has
//   dismissed the banner but NOT opted-in; extraction stays gated OFF.
//
// Either way, ``memory_consent_banner_shown_at`` transitions from NULL to
// non-NULL exactly once — the FE hides the banner permanently after the
// first click. Re-opting-in later happens via ``PATCH /gdpr/consent``
// from the ``/settings/memory`` panel (Wave 2).

export interface MemoryConsentAcknowledgeRequest {
  readonly accepted: boolean;
}

export interface MemoryConsentAcknowledgeResponse {
  readonly status: "ok" | "error";
  readonly memory_consent_at: string | null;
  readonly memory_consent_banner_shown_at: string;
}

export async function acknowledgeMemoryConsent(
  body: MemoryConsentAcknowledgeRequest,
): Promise<MemoryConsentAcknowledgeResponse> {
  return apiClient.post<MemoryConsentAcknowledgeResponse>(
    "/gdpr/memory-consent-acknowledge",
    body,
  );
}
